import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FileSpreadsheet, Gauge, Truck, MessageCircle, ArrowRight } from 'lucide-react';

const HERO_IMG = 'https://images.unsplash.com/photo-1724582586529-62622e50c0b3?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000';
const FEATURES = [
  { icon: FileSpreadsheet, title: 'BOQ Management', text: 'Design-to-site BOQs with live rates, revisions and one-click client & vendor publishing.' },
  { icon: Gauge, title: 'Commercial Cockpit', text: 'Consultancy milestones, billable-so-far and receivables in a single real-time snapshot.' },
  { icon: Truck, title: 'Vendor Automation', text: 'Quote requests, rate contracts, payouts and a vendor portal that runs itself.' },
  { icon: MessageCircle, title: 'WhatsApp Notifications', text: 'Clients and vendors get every approval, bill and payment update where they already chat.' },
];
const STATS = [['Trusted by', 'interior firms across India'], ['One workspace', 'design · costing · site · billing'], ['Realtime', 'every number synced everywhere']];
const rise = (i = 0) => ({ style: { animationDelay: `${i * 90}ms` } });

const useFonts = () => useEffect(() => {
  if (document.getElementById('raw-landing-fonts')) return;
  const l = document.createElement('link');
  l.id = 'raw-landing-fonts';
  l.rel = 'stylesheet';
  l.href = 'https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=Manrope:wght@400;500;600;700&display=swap';
  document.head.appendChild(l);
}, []);

const Nav = () => (
  <nav className="fixed top-4 inset-x-4 md:inset-x-10 z-30 lg-glass rounded-full border px-5 py-2.5 flex items-center justify-between" data-testid="landing-nav">
    <Link to="/" className="flex items-baseline gap-2" data-testid="landing-logo">
      <span className="text-2xl font-bold tracking-tight text-stone-900">RAW</span>
      <span className="hidden sm:inline text-[11px] uppercase tracking-[0.25em] text-stone-500">Realtime Automation Workflow</span>
    </Link>
    <div className="flex items-center gap-2">
      <Link to="/login" className="px-4 py-2 text-sm rounded-full text-stone-700 hover:bg-white/70 transition-colors" data-testid="landing-nav-login">Log In</Link>
      <Link to="/signup" className="px-4 py-2 text-sm rounded-full lg-btn-primary transition-transform hover:-translate-y-px" data-testid="landing-nav-signup">Get Started</Link>
    </div>
  </nav>
);

const Hero = () => (
  <section className="relative min-h-[100vh] flex items-end md:items-center px-4 md:px-10 pt-28 pb-16 overflow-hidden" data-testid="landing-hero">
    <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover" />
    <div className="absolute inset-0 bg-gradient-to-t from-stone-950/70 via-stone-900/20 to-transparent" />
    <div className="lg-rise relative max-w-2xl lg-glass rounded-[2rem] border p-8 md:p-12" style={{ background: 'rgba(255,255,255,0.62)' }}>
      <div className="text-[11px] uppercase tracking-[0.3em] text-stone-600 mb-4">RAW · Realtime Automation Workflow</div>
      <h1 className="text-4xl sm:text-5xl lg:text-6xl text-stone-900 leading-[1.05]" style={{ fontFamily: "'Instrument Serif', serif" }} data-testid="landing-hero-title">
        Run every interior project <em>from one living workflow.</em>
      </h1>
      <p className="mt-5 text-base text-stone-700 max-w-lg" data-testid="landing-hero-value">
        Design, costing, BOQs, vendors and billing move together in real time, so your studio quotes faster and gets paid sooner.
      </p>
      <div className="mt-8 flex flex-wrap gap-3">
        <Link to="/signup" className="inline-flex items-center gap-2 px-6 py-3 rounded-full lg-btn-primary text-sm font-semibold transition-transform hover:-translate-y-px" data-testid="landing-get-started-btn">
          Get Started <ArrowRight size={16} />
        </Link>
        <Link to="/login" className="inline-flex items-center px-6 py-3 rounded-full lg-btn-glass text-sm font-semibold text-stone-800 transition-transform hover:-translate-y-px" data-testid="landing-login-btn">Log In</Link>
      </div>
    </div>
  </section>
);

const Features = () => (
  <section className="px-4 md:px-10 py-24" data-testid="landing-features">
    <div className="max-w-6xl">
      <h2 className="text-base md:text-lg uppercase tracking-[0.25em] text-stone-500">What RAW automates</h2>
      <p className="text-3xl md:text-4xl text-stone-900 mt-3 max-w-2xl" style={{ fontFamily: "'Instrument Serif', serif" }}>Built for the way design studios actually work.</p>
    </div>
    <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
      {FEATURES.map(({ icon: Icon, title, text }, i) => (
        <div key={title} style={rise(i).style} className="lg-rise lg-glass rounded-3xl border p-6 hover:-translate-y-1" data-testid={`landing-feature-${i}`}>
          <div className="w-11 h-11 rounded-2xl lg-btn-glass flex items-center justify-center"><Icon size={20} className="text-stone-800" /></div>
          <h3 className="mt-5 text-lg font-semibold text-stone-900">{title}</h3>
          <p className="mt-2 text-sm text-stone-600 leading-relaxed">{text}</p>
        </div>
      ))}
    </div>
  </section>
);

const Stats = () => (
  <section className="px-4 md:px-10 pb-24" data-testid="landing-stats">
    <div className="lg-glass rounded-[2rem] border grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-stone-200/70">
      {STATS.map(([big, small]) => (
        <div key={big} className="p-8">
          <div className="text-3xl text-stone-900" style={{ fontFamily: "'Instrument Serif', serif" }}>{big}</div>
          <div className="text-sm text-stone-600 mt-1">{small}</div>
        </div>
      ))}
    </div>
  </section>
);

const Footer = () => (
  <footer className="px-4 md:px-10 py-10 border-t border-stone-200/70 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-sm text-stone-500" data-testid="landing-footer">
    <div><span className="font-bold text-stone-900">RAW</span> · © {new Date().getFullYear()} RAW — Realtime Automation Workflow. All rights reserved.</div>
    <div className="flex gap-5">
      <Link to="/legal/terms" className="hover:text-stone-900 transition-colors" data-testid="landing-terms-link">Terms</Link>
      <Link to="/legal/privacy" className="hover:text-stone-900 transition-colors" data-testid="landing-privacy-link">Privacy</Link>
    </div>
  </footer>
);

export default function LandingPage() {
  useFonts();
  return (
    <div className="lg-scope min-h-screen bg-stone-50 text-stone-900" style={{ fontFamily: "'Manrope', sans-serif" }} data-testid="landing-page">
      <Nav />
      <Hero />
      <Features />
      <Stats />
      <Footer />
    </div>
  );
}
